import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { Button as UiButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  SectionHeading,
  SubHeading,
  plural,
  relativeDay,
} from "../components/kit";
import { useBusyAction, type Outcome } from "../lib/useBusyAction";
import {
  CLINIC_REPORTABLE_RECEIPTS,
  presentReceiptStatus,
} from "@shared/status";
import { formatCents } from "@shared/money";

/**
 * Confirm a delivery.
 *
 * The overwhelmingly common case is "the boxes came and they're all there", so
 * that is one tap — "All arrived". Reporting a problem is a second path, not a
 * tax on the first.
 *
 * This is also where the invoice is raised, because Gloria bills what a clinic
 * *received*. A clinic that got four of five boxes is charged for four, and a
 * damaged line is never billed at all — no charge, no credit note, no
 * conversation.
 *
 * Sizing is the registry's. The one place the large size is spent is the pair
 * of counting controls — `outline` at `icon-lg`, the same control kit's own
 * `<QtyStepper size="lg">` draws — plus the buttons that commit the count, so
 * a person counting boxes on a trolley is aiming at the biggest targets on the
 * screen. The consequence of each outcome is still written under it in words
 * rather than left to be inferred from a colour.
 */

/** The outcomes a clinic can report, from `@shared/status` — the same list the
 *  Convex validator behind `receiving.confirm` is built from. */
type ReportableReceipt = (typeof CLINIC_REPORTABLE_RECEIPTS)[number];

type LineState = {
  qtyReceived: number;
  status: ReportableReceipt;
};

/**
 * The buttons, derived rather than retyped — minus `partial`.
 *
 * `partial` is not a thing anyone taps: `receiving.confirm` derives it from the
 * quantity ("tapping received while entering 3 of 5 is a partial, whatever the
 * button said"), so offering it would be a second, contradictory way to say
 * what the stepper already says. Every *other* outcome comes straight off the
 * shared list, so a new one — "wrong item", say — is offered here the moment
 * the server accepts it.
 */
const RECEIPT_BUTTONS: readonly ReportableReceipt[] =
  CLINIC_REPORTABLE_RECEIPTS.filter((status) => status !== "partial");

/**
 * What this outcome does to the money and to the request, in one line.
 *
 * Read off `convex/receiving.ts` rather than invented: a missing or damaged
 * line is never stocked and never billed; a "received" line short of what was
 * sent is stored as a partial and billed for what arrived; a back order is
 * pushed back onto the request line it came from.
 */
function consequenceOf(state: LineState, qtyDispatched: number): string | null {
  if (state.status === "damaged" || state.status === "missing") {
    return "Not charged on the invoice.";
  }
  if (state.status === "back-ordered") {
    return "Goes back on the request as a back order.";
  }
  if (state.qtyReceived < qtyDispatched) {
    return `Recorded as partial — ${state.qtyReceived} of ${qtyDispatched} charged.`;
  }
  return null;
}

export function Receive() {
  return (
    <ScreenBoundary
      title="Deliveries could not load"
      heading={<SectionHeading title="Receive" />}
      onError={(error, info) =>
        console.error("Receive screen failed:", error, info.componentStack)
      }
    >
      <ReceiveScreen />
    </ScreenBoundary>
  );
}

function ReceiveScreen() {
  const deliveries = useQuery(api.receiving.awaiting, {});
  const confirmAll = useMutation(api.receiving.confirmAll);
  const confirm = useMutation(api.receiving.confirm);

  const [openId, setOpenId] = useState<string | null>(null);
  const [lineState, setLineState] = useState<Record<string, LineState>>({});
  const { busy, run } = useBusyAction();

  if (deliveries === undefined) {
    return (
      <div data-screen-state="loading" className="space-y-4">
        <SectionHeading title="Receive" />
        <Loading rows={3} label="Loading deliveries" />
      </div>
    );
  }

  /**
   * Say what actually happened — and never assert an amount, or the absence of
   * one, to a role that cannot see money.
   *
   * This was the worst bug in the app. The description branched on
   * `invoiceNumber`, which the server deliberately withholds from an assistant
   * (she is the one role that sees no money anywhere in this product), so the
   * tap that raised a real invoice for a $1,327 delivery told her "Nothing
   * chargeable on this delivery." Verified in rehearsal: five invoices before
   * the tap, six after.
   *
   * The fix is copy, not data. The permission boundary is right and stays
   * exactly as it is — the client does not fetch the invoice, and nothing here
   * asks the server for a figure it chose not to send.
   *
   * So: when the caller may see money, the invoice is named as before. When she
   * may not, the sentence describes *the delivery* — what is now booked in and
   * what happens next — and says nothing whatsoever about charging, not
   * charging, invoices or totals. Silence is honest; "nothing chargeable" was
   * not.
   *
   * The two titles are load-bearing: `tools/clickcount.mjs` and
   * `tools/demo.mjs` both wait on /Delivery confirmed|Delivery recorded/.
   */
  const outcomeOf = (result: {
    receiptStatus: string;
    invoiceRaised: boolean;
    invoiceNumber: string | null;
    invoiceTotalCents: number | null;
    linesWithheld: number;
  }): Outcome => {
    const title =
      result.receiptStatus === "received" ? "Delivery confirmed" : "Delivery recorded";
    const short = result.linesWithheld;

    // The money branch. Only ever reached by a role the server sent a number
    // to, because it is that number this branch is built out of.
    if (result.invoiceNumber) {
      const withheld =
        short > 0 ? ` — ${short} ${plural(short, "line")} not charged` : "";
      const description = `Invoice ${result.invoiceNumber}${
        result.invoiceTotalCents !== null
          ? ` for ${formatCents(result.invoiceTotalCents)}`
          : ""
      } raised${withheld}.`;
      return { happened: title, next: description, check: short > 0 };
    }

    // No money, in either direction. What arrived, and who picks it up next.
    if (short > 0) {
      return {
        happened: title,
        next:
          `${short} ${plural(short, "item")} logged as not arrived. Your manager ` +
          "sees that and follows it up with the warehouse.",
        check: true,
      };
    }

    return {
      happened: title,
      next: "Everything is booked in against your clinic. Nothing else for you to do.",
    };
  };

  const allArrived = (dispatchId: Id<"dispatches">) =>
    run(
      async () => {
        const result = await confirmAll({ dispatchId });
        setOpenId(null);
        return result;
      },
      { failed: "Could not confirm this delivery", done: outcomeOf },
    );

  const submitDetailed = (
    dispatchId: Id<"dispatches">,
    lines: Array<{ _id: Id<"dispatchLines">; qtyDispatched: number }>,
  ) =>
    run(
      async () => {
        const result = await confirm({
          dispatchId,
          lines: lines.map((line) => {
            const state = lineState[line._id] ?? {
              qtyReceived: line.qtyDispatched,
              status: "received" as const,
            };
            return {
              dispatchLineId: line._id,
              qtyReceived: state.qtyReceived,
              status: state.status,
            };
          }),
        });
        setOpenId(null);
        setLineState({});
        return result;
      },
      { failed: "Could not record what arrived", done: outcomeOf },
    );

  if (deliveries.length === 0) {
    return (
      <div data-screen-state="ready" className="space-y-4">
        <SectionHeading title="Receive" />
        <Empty
          tone="good"
          title="Nothing in transit"
          body="Every delivery has been confirmed. New ones appear here the moment the warehouse dispatches them."
        />
      </div>
    );
  }

  return (
    <div data-screen-state="ready" className="space-y-4 pb-8">
      <SectionHeading
        title="Receive"
        subtitle={`${deliveries.length} deliver${
          deliveries.length === 1 ? "y" : "ies"
        } to confirm`}
      />

      {/* Two across at desktop while they are closed; the one being counted
          opens to the full width, because that is where the steppers live. */}
      <div className="space-y-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0">
        {deliveries.map((delivery) => {
          const isOpen = openId === delivery._id;
          return (
            /* Stock `ui/card`, laid out through its own slots. `p-0`/`gap-0`
               beat the registry's `py-6`/`gap-6`, which exist for the padded
               header-and-content shape this card does not have: the counting
               panel and the button row are full-bleed sections divided by a
               rule, so the padding belongs to them and not to the card. */
            <Card
              key={delivery._id}
              className={cn(
                "gap-0 overflow-hidden p-0",
                isOpen ? "lg:col-span-2" : "",
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-bold tracking-tight">
                      {delivery.number}
                    </div>
                    <div className="mt-0.5 text-sm text-muted-foreground">
                      {delivery.clinicName}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {delivery.lineCount} {plural(delivery.lineCount, "item")} ·{" "}
                      {delivery.totalUnits} {plural(delivery.totalUnits, "unit")} ·
                      sent {relativeDay(delivery.dispatchedAt)} by{" "}
                      {delivery.deliveredBy}
                    </div>
                  </div>
                  {/* A duration is not a status, and red in this app means one
                      thing: requested / needs ordering. "3d in transit" wearing
                      the same red as an unordered line put a fourth meaning on
                      the load-bearing colour. It reads as what it is — how long
                      this has been on the road — in the muted type the rest of
                      the card's metadata uses, going amber past four days,
                      which is the urgency family a "this is taking a while"
                      qualifier belongs to. */}
                  {delivery.daysInTransit >= 2 ? (
                    <div
                      className={cn(
                        "shrink-0 text-xs whitespace-nowrap",
                        delivery.daysInTransit >= 4
                          ? "font-semibold text-amber-800"
                          : "text-muted-foreground",
                      )}
                    >
                      {delivery.daysInTransit} {plural(delivery.daysInTransit, "day")} on
                      the road
                    </div>
                  ) : null}
                </div>

                <ul className="mt-3 space-y-1 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:space-y-0">
                  {delivery.lines.map((line) => (
                    <li
                      key={line._id}
                      className="flex gap-2 text-sm text-muted-foreground"
                    >
                      <span className="num w-8 shrink-0 text-right font-bold">
                        ×{line.qtyDispatched}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{line.itemName}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              {isOpen ? (
                <div className="space-y-3 border-t bg-muted/50 p-4">
                  <SubHeading>What actually arrived?</SubHeading>
                  <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
                    {delivery.lines.map((line) => {
                      const state = lineState[line._id] ?? {
                        qtyReceived: line.qtyDispatched,
                        status: "received" as const,
                      };
                      const consequence = consequenceOf(state, line.qtyDispatched);
                      const set = (next: Partial<LineState>) =>
                        setLineState((s) => ({
                          ...s,
                          [line._id]: { ...state, ...next },
                        }));
                      // `rounded-lg`, not the card radius — an inner panel
                      // inside a card, so it is deliberately not `Card`.
                      return (
                        <div
                          key={line._id}
                          className="rounded-lg border bg-card p-3"
                        >
                          <div className="truncate text-sm font-semibold">
                            {line.itemName}
                          </div>

                          {/* The counting controls, and the one place on this
                              screen the large size is spent: `outline` at
                              `icon-lg`, which is exactly what kit's own
                              `<QtyStepper size="lg">` renders. It is not
                              reused wholesale because this stepper counts
                              *down to zero* against what was dispatched — the
                              kit's clamps to 1–10,000 and puts an editable
                              input in the middle, and a delivery of zero here
                              is a real answer. */}
                          <div className="mt-2 flex items-center gap-2">
                            <span className="w-16 shrink-0 text-xs leading-tight text-muted-foreground">
                              Sent {line.qtyDispatched}, got
                            </span>
                            <UiButton
                              type="button"
                              variant="outline"
                              size="icon-lg"
                              aria-label="One fewer"
                              onClick={() =>
                                set({ qtyReceived: Math.max(0, state.qtyReceived - 1) })
                              }
                              className="shrink-0 text-lg"
                            >
                              −
                            </UiButton>
                            <span
                              className={cn(
                                "num w-12 text-center text-lg font-semibold",
                                state.qtyReceived < line.qtyDispatched &&
                                  "text-red-700",
                              )}
                            >
                              {state.qtyReceived}
                            </span>
                            <UiButton
                              type="button"
                              variant="outline"
                              size="icon-lg"
                              aria-label="One more"
                              onClick={() =>
                                set({
                                  qtyReceived: Math.min(
                                    line.qtyDispatched,
                                    state.qtyReceived + 1,
                                  ),
                                })
                              }
                              className="shrink-0 text-lg"
                            >
                              +
                            </UiButton>
                          </div>

                          {/* The outcome is a choice, so the selected one is
                              the primary surface — the same language as a
                              filter chip. Red here would compete with the
                              status pills that mean something else.

                              **`aria-pressed` is load-bearing** and survived
                              the restyle deliberately: it is what makes this
                              row of buttons a segmented toggle to assistive
                              tech. */}
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {RECEIPT_BUTTONS.map((option) => (
                              <Button
                                key={option}
                                variant={
                                  state.status === option ? "default" : "outline"
                                }
                                aria-pressed={state.status === option}
                                onClick={() => set({ status: option })}
                                className="flex-1"
                              >
                                {presentReceiptStatus(option).label}
                              </Button>
                            ))}
                          </div>

                          {consequence ? (
                            <p className="mt-2 text-xs font-medium text-amber-900">
                              {consequence}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="lg"
                      className="flex-1 lg:flex-none lg:px-8"
                      disabled={busy}
                      onClick={() => submitDetailed(delivery._id, delivery.lines)}
                    >
                      Record what arrived
                    </Button>
                    <Button variant="ghost" size="lg" onClick={() => setOpenId(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 border-t bg-muted/50 p-3">
                  <Button
                    size="lg"
                    className="flex-1"
                    disabled={busy}
                    onClick={() => allArrived(delivery._id)}
                  >
                    All arrived
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setOpenId(delivery._id)}
                  >
                    Something's off
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
